import { useMemo, useState } from 'react';
import type { AuditLogResponse, AuditSource } from '@orion/contracts';
import { PageHeader } from '../../../app/shell.js';
import { disambiguateNames } from '../../../lib/disambiguate-names.js';
import { useMembershipsQuery } from '../users/api.js';
import { auditExportUrl, useAuditLogsQuery } from './api.js';

const PAGE_SIZE = 25;

const isDesignAuditPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'AdminAudit';

const ENTITY_TYPE_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'contact', label: 'Contact' },
  { value: 'company', label: 'Company' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'task', label: 'Task' },
  { value: 'session', label: 'Session' },
  { value: 'tenant_membership', label: 'User' },
  { value: 'role', label: 'Role' },
  { value: 'team', label: 'Team' },
  { value: 'api_key', label: 'API key' },
  { value: 'webhook_subscription', label: 'Webhook' },
  { value: 'webhook_delivery', label: 'Webhook delivery' },
  { value: 'web_form', label: 'Web form' },
  { value: 'import_job', label: 'Import' },
  { value: 'email_template', label: 'Email template' },
  { value: 'tenant_settings', label: 'Setting' },
  { value: 'tenant_domain', label: 'Domain' },
  { value: 'auth_provider', label: 'SSO' },
  { value: 'attachment', label: 'Attachment' },
  { value: 'activity', label: 'Activity' },
];

const DATE_RANGE_OPTIONS = [
  { value: '', label: 'All time' },
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

const SOURCE_LABEL: Record<AuditSource, string> = { web: 'Web', api: 'API', sso: 'SSO', system: 'System' };

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const datePart = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  return `${datePart} ${timePart}`;
}

function humanizeKey(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  if (typeof v === 'string') return v.length > 40 ? `${v.slice(0, 40)}…` : v;
  return String(v);
}

/** A one-line description built from the real `changes` payload — not the
 * design's fully-composed per-action sentences ("Status New → Qualified"
 * with resolved display names). Those would need every writer to store
 * human-readable before/after labels instead of raw ids, which is a bigger
 * change than this screen; this reads whatever `changes` actually holds. */
function summarize(row: AuditLogResponse): string {
  if (!row.changes) return '';
  const entries = Object.entries(row.changes);
  if (entries.length === 0) return '';
  if (entries.length > 1) return `${entries.length} fields changed`;
  const [key, tuple] = entries[0]!;
  const [oldV, newV] = tuple;
  const label = humanizeKey(key);
  if (oldV == null && newV != null) return `${label}: ${formatValue(newV)}`;
  if (oldV != null && newV == null) return `${label} removed`;
  return `${label} ${formatValue(oldV)} → ${formatValue(newV)}`;
}

function actionPillClass(action: string): string {
  if (action.includes('failed') || action.includes('disabled')) return 'bg-danger-soft text-danger border border-danger-soft';
  if (action.startsWith('role.') || action.startsWith('sso.')) return 'bg-violet-soft text-violet border border-violet-soft';
  return 'bg-surface-subtle text-ink border border-divider';
}

function AvatarDot({ name, isSystem }: { name: string; isSystem: boolean }) {
  if (isSystem) {
    return (
      <span className="flex size-4.5 shrink-0 items-center justify-center rounded-full border border-border bg-surface-subtle">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-text-secondary">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
        </svg>
      </span>
    );
  }
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const hue = Math.abs([...name].reduce((h, c) => h * 31 + c.charCodeAt(0), 0)) % 6;
  const colors = ['#0F6B63', '#B42318', '#3A3F8F', '#8A4B06', '#2E47D6', '#1E7A4C'];
  return (
    <span className="flex size-4.5 shrink-0 items-center justify-center rounded-full text-[8px] font-[650] text-white" style={{ background: colors[hue] }}>
      {initials}
    </span>
  );
}

function ChangesDiff({ row }: { row: AuditLogResponse }) {
  const changes = row.changes;
  if (!changes) return null;
  const oldObj = Object.fromEntries(Object.entries(changes).map(([k, [o]]) => [k, o]));
  const newObj = Object.fromEntries(Object.entries(changes).map(([k, [, n]]) => [k, n]));
  const removedCount = Object.values(oldObj).filter((v) => v != null).length;
  const addedCount = Object.values(newObj).filter((v) => v != null).length;

  return (
    <div className="grid gap-3 border-y border-border bg-surface-subtle px-4 py-2.5" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) 236px' }}>
      <div className="overflow-hidden rounded-control border border-border bg-surface">
        <div className="flex h-6.5 items-center justify-between px-2.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
          Old value
          {removedCount > 0 && <span className="font-medium normal-case tracking-normal text-danger">{removedCount} removed</span>}
        </div>
        <pre className="m-0 overflow-x-auto px-2.5 py-1 font-mono text-[11.5px] leading-[18px] text-text-secondary">{JSON.stringify(oldObj, null, 2)}</pre>
      </div>
      <div className="overflow-hidden rounded-control border border-border bg-surface">
        <div className="flex h-6.5 items-center justify-between px-2.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
          New value
          {addedCount > 0 && <span className="font-medium normal-case tracking-normal text-success">{addedCount} added</span>}
        </div>
        <pre className="m-0 overflow-x-auto px-2.5 py-1 font-mono text-[11.5px] leading-[18px] text-text-secondary">{JSON.stringify(newObj, null, 2)}</pre>
      </div>
      <dl className="m-0 flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Request ID</dt>
          <dd className="m-0 font-mono text-xs">{row.requestId ?? '—'}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">User agent</dt>
          <dd className="m-0 font-mono text-[11px] leading-[15px] text-text-secondary">{row.userAgent ?? '—'}</dd>
        </div>
      </dl>
    </div>
  );
}

export function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [actorId, setActorId] = useState('');
  const [entityType, setEntityType] = useState('');
  const [ip, setIp] = useState('');
  const [rangeDays, setRangeDays] = useState('7');
  const [expandedId, setExpandedId] = useState<string | null>(isDesignAuditPreview ? 'aud-2' : null);

  // `Date.now()` must not be recomputed on every render: that would change
  // `dateFrom` (and so `filters`, the query key) on every render, and since
  // a resolved fetch itself triggers a re-render, React Query would never
  // stop refetching — the table stays permanently empty/loading because no
  // request ever gets to be the latest one for more than an instant.
  // Memoized on `rangeDays` so "now" is captured once per range selection.
  const dateFrom = useMemo(() => (rangeDays ? new Date(Date.now() - Number(rangeDays) * 86_400_000).toISOString() : undefined), [rangeDays]);
  const filters = { page, actorId: actorId || undefined, action: search || undefined, entityType: entityType || undefined, ip: ip || undefined, dateFrom };
  const { data, isLoading } = useAuditLogsQuery(filters);
  const { data: memberships } = useMembershipsQuery();
  const actorLabels = useMemo(() => disambiguateNames(memberships?.data ?? []), [memberships]);
  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const rangeLabel = DATE_RANGE_OPTIONS.find((o) => o.value === rangeDays)?.label.toLowerCase() ?? 'all time';

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Administration', to: '/admin' }, { label: 'Audit log' }]} />
      <main className="flex flex-col gap-3.5 overflow-hidden px-8 py-7" style={{ height: 'calc(100vh - 60px)' }}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">Audit log</h1>
            <p className="text-sm leading-5 text-text-muted">Every sign-in, permission change and record update, with who did it and from where.</p>
            <span className="mt-1 flex items-center gap-1.5 text-xs font-medium text-success">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              Tamper-evident <span className="font-normal text-text-muted">· retained 365 days · visible to Admins only</span>
            </span>
          </div>
          <a href={auditExportUrl(filters)} className="flex h-9 shrink-0 items-center gap-2 rounded-control border border-border-strong bg-surface px-3.5 text-sm font-semibold text-ink">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 4v12M7 11l5 5 5-5" />
              <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
            </svg>
            Export CSV
          </a>
        </div>

        <div role="search" className="flex flex-wrap items-center gap-2">
          <label className="flex h-9 w-58 items-center gap-2 rounded-control border border-border-strong bg-surface px-3">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-text-secondary">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <span className="sr-only">Search audit log</span>
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by action code…"
              className="w-full text-sm outline-none"
            />
          </label>
          <select
            value={actorId}
            onChange={(e) => {
              setActorId(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] font-semibold text-text-secondary"
          >
            <option value="">Actor</option>
            {(memberships?.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {actorLabels.get(m.id) ?? m.displayName}
              </option>
            ))}
          </select>
          <select
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] font-semibold text-text-secondary"
          >
            <option value="">Entity type</option>
            {ENTITY_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={rangeDays}
            onChange={(e) => {
              setRangeDays(e.target.value);
              setPage(1);
            }}
            className="flex h-9 items-center gap-1.5 rounded-control border border-primary-soft bg-primary-soft px-2.5 text-[13px] font-semibold text-primary"
          >
            {DATE_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            value={ip}
            onChange={(e) => {
              setIp(e.target.value);
              setPage(1);
            }}
            placeholder="IP address"
            className="h-9 w-32 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] font-medium text-text-secondary outline-none"
          />
        </div>

        <section role="table" aria-label="Audit events" className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-dialog border border-border bg-surface shadow-card">
          <div
            role="row"
            className="grid h-9 items-center gap-1.5 px-3 text-xs font-semibold text-text-muted"
            style={{ gridTemplateColumns: '128px 112px 180px 140px minmax(0,1fr) 100px 44px', background: 'var(--color-surface-subtle)', borderBottom: '1px solid var(--color-border)' }}
          >
            <span role="columnheader" className="pl-4.5">
              Timestamp ↓
            </span>
            <span role="columnheader">Actor</span>
            <span role="columnheader">Action</span>
            <span role="columnheader">Entity</span>
            <span role="columnheader">Summary</span>
            <span role="columnheader">IP</span>
            <span role="columnheader">Source</span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {!isLoading &&
              rows.map((row) => {
                const isExpanded = expandedId === row.id;
                const isFailed = row.action.includes('failed');
                return (
                  <div key={row.id}>
                    <button
                      type="button"
                      role="row"
                      aria-expanded={isExpanded}
                      onClick={() => setExpandedId(isExpanded ? null : row.id)}
                      className="grid h-8 w-full items-center gap-1.5 border-b border-divider px-3 text-left text-[13px] last:border-b-0 hover:bg-surface-subtle"
                      style={{
                        gridTemplateColumns: '128px 112px 180px 140px minmax(0,1fr) 100px 44px',
                        background: isExpanded ? 'var(--color-primary-soft)' : isFailed ? '#FEF8EE' : undefined,
                      }}
                    >
                      <span className="flex items-center gap-1.5 whitespace-nowrap pl-1 font-mono text-xs text-text-secondary">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2.2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          className={`shrink-0 transition-transform ${isExpanded ? 'rotate-90 text-primary' : 'text-text-faint'}`}
                        >
                          <path d="m9 6 6 6-6 6" />
                        </svg>
                        {formatTimestamp(row.occurredAt)}
                      </span>
                      <span className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap">
                        <AvatarDot name={row.actor?.displayName ?? 'System'} isSystem={!row.actor} />
                        <span className="truncate">{row.actor?.displayName ?? 'System'}</span>
                      </span>
                      <span>
                        <span className={`inline-flex h-5 items-center rounded-control px-1.5 font-mono text-[11px] ${actionPillClass(row.action)}`}>{row.action}</span>
                      </span>
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap text-text-secondary">
                        <span className="text-text-muted">{ENTITY_TYPE_OPTIONS.find((e) => e.value === row.entityType)?.label ?? row.entityType} ·</span> {row.entityId?.slice(0, 8) ?? ''}
                      </span>
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap">{summarize(row)}</span>
                      <span className="font-mono text-xs text-text-secondary">{row.ip ?? '—'}</span>
                      <span className="text-xs text-text-secondary">{SOURCE_LABEL[row.source]}</span>
                    </button>
                    {isExpanded && <ChangesDiff row={row} />}
                  </div>
                );
              })}
          </div>
          <div className="flex h-11 shrink-0 items-center justify-between border-t border-border bg-surface-subtle px-4 text-[13px] text-text-muted">
            <span>
              {total === 0 ? '0' : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)}`} of {total.toLocaleString()} events · {rangeLabel}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="flex size-8 items-center justify-center rounded-control border border-border bg-surface disabled:text-text-faint"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m15 6-6 6 6 6" />
                </svg>
              </button>
              <button
                type="button"
                aria-label="Next page"
                disabled={page * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
                className="flex size-8 items-center justify-center rounded-control border border-border-strong bg-surface disabled:text-text-faint"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
